import { Typography } from '@mui/material';
import classNames from 'classnames';
import { sortBy } from 'lodash-es';
import React, { Fragment, PureComponent, ReactNode } from 'react';

import { Tree, TreeProps } from '../Tree';
import { TreeNode } from '../TreeNode';
import css from './JsonTree.module.scss';

const INTERNAL_NODE_TYPES = ['object', 'array'];
const LEAF_NODE_TYPES = ['boolean', 'number', 'string'];

export interface JsonTreeProps extends TreeProps {
  data: { [key: string]: any };
  nodePosition?: 'end' | 'bottom';
  showCount: boolean;
  showEmpty: boolean;
  valueRenderer?: (node: ReactNode, key: string) => ReactNode;
  keyRenderer?: (leafKey: ReactNode, fullKey: string) => ReactNode;
}

export interface JSONTreeNode {
  label: string;
  type: string;
  count?: number;
  data: any;
}

const getType = (data: any) => {
  if (data === null) {
    return 'null';
  } else if (Array.isArray(data)) {
    return 'array';
  }
  return typeof data;
};

export class JsonTree extends PureComponent<JsonTreeProps> {
  static defaultProps: Partial<JsonTreeProps> = {
    nodePosition: 'end',
    showCount: true,
    showEmpty: true,
  };

  // complexity 18
  // eslint-disable-next-line complexity
  parseJson(data: any, label?: string) {
    const { showEmpty } = this.props;
    const type = getType(data);
    label = label === undefined ? type : label;

    switch (type) {
      case 'object':
        const keys = Object.keys(data);
        const parsedObjData = sortBy(
          keys.reduce((parsedItems, key) => {
            const value = data[key];
            const parsed = this.parseJson(value, key);

            if (showEmpty || (!showEmpty && parsed !== null)) {
              parsedItems.push(parsed);
            }

            return parsedItems;
          }, [] as any),
          'label'
        );

        return showEmpty || (!showEmpty && parsedObjData.length > 0)
          ? {
              type,
              data: parsedObjData,
              count: parsedObjData.length,
              label,
            }
          : null;
      case 'array':
        const parsedArrayData = data.reduce((parsedItems, value) => {
          const parsed = this.parseJson(value);

          if (showEmpty || (!showEmpty && parsed !== null)) {
            parsedItems.push(parsed);
          }

          return parsedItems;
        }, [] as any);

        return parsedArrayData !== null &&
          (showEmpty || (!showEmpty && parsedArrayData.length > 0))
          ? {
              type,
              data: parsedArrayData,
              count: parsedArrayData.length,
              label,
            }
          : null;
      case 'string':
        return showEmpty || (!showEmpty && data.length > 0)
          ? {
              type: 'string',
              data: data.length === 0 ? '""' : data,
              label,
            }
          : null;
      case 'boolean':
        return {
          type,
          data: String(data),
          label,
        };
      case 'null':
        return showEmpty
          ? {
              type: 'string',
              data: 'null',
              label,
            }
          : null;
      default:
        return {
          type,
          data,
          label,
        };
    }
  }

  renderExpandableNode(child: JSONTreeNode) {
    const { showCount } = this.props;

    return (
      <Fragment>
        <Typography variant='code' color='text.secondary'>
          {child.label}
        </Typography>
        {showCount && (
          <Fragment>
            <Typography variant='code' mr={0.75}>
              :
            </Typography>
            <Typography variant='code' ml={0.75}>
              {child.count} items
            </Typography>
          </Fragment>
        )}
      </Fragment>
    );
  }

  renderKey(child: JSONTreeNode, path: (string | number)[] = []) {
    const { keyRenderer } = this.props;

    const key = (
      <Typography variant='code' color='text.secondary'>
        {child.label}
      </Typography>
    );

    const fullPath = [...path];
    fullPath.shift(); // remove top-level "object" key

    const serializedFullPath = JSON.stringify(fullPath);

    return keyRenderer ? keyRenderer(key, serializedFullPath) : <>{key}</>;
  }

  renderNode(child: JSONTreeNode, path: (string | number)[]) {
    const { valueRenderer, nodePosition } = this.props;

    return (
      <div
        className={classNames(css.nodeContainer, {
          [css.vertical]: nodePosition === 'bottom',
        })}
      >
        <div className={css.labelContainer}>
          <Typography variant='code' className={css.nodeLabel}>
            {this.renderKey(child, path)}
          </Typography>
          <Typography variant='code' mr={0.75}>
            :
          </Typography>
        </div>
        <div>
          <Typography variant='code'>
            {valueRenderer
              ? valueRenderer(child.data, child.label)
              : child.data.toString()}
          </Typography>
        </div>
      </div>
    );
  }

  renderTree(
    children: JSONTreeNode[],
    path: (string | number)[] = [],
    isArrayElement: boolean = false
  ) {
    return children.map((child, index) => {
      const nextPath = [...path, isArrayElement ? index : child.label];

      return (
        <TreeNode
          expanded={true}
          key={`node-${child.label}-${index}`}
          label={
            <span>
              {INTERNAL_NODE_TYPES.includes(child.type) &&
                this.renderExpandableNode(child)}
              {LEAF_NODE_TYPES.includes(child.type) &&
                this.renderNode(child, nextPath)}
            </span>
          }
        >
          {child.count! > 0 &&
            this.renderTree(child.data, nextPath, child.type === 'array')}
        </TreeNode>
      );
    });
  }

  render() {
    const { data, ...rest } = this.props;
    const parsed = this.parseJson(data);

    return (
      <Tree {...rest}>
        {parsed !== null ? this.renderTree([parsed], []) : 'No data'}
      </Tree>
    );
  }
}
